import styled from 'styled-components';
import {
  Flex,
  IconButton,
  isUserWithRoles,
  SectionIconButton,
  TableIcon,
  useCombinedUser,
  UserRoles,
} from '@equifinance/shared';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Application } from '../../../contexts/applications';
import ReactivateModal from '../modals/ReactivateModal';
import RejectModal from '../modals/RejectModal';

const ActionsColumnContainer = styled(Flex)`${({ theme }) => `
  width: 100%;
  max-width: 100%;
  align-items: center;
  justify-content: end;
  padding: ${theme.padding[1]};
  gap: ${theme.padding[5]};

  ${SectionIconButton} {
    color: ${theme.colours.primary[1]};
    :hover:not([disabled]) {
      transform: none;
    }
  }
`}`;

const ActionButton = styled(IconButton)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  ${TableIcon} {
    font-size: ${theme.fontSize[3]};
  }
`}`;

const defaultModal = { id: '', open: false };

const PlainLink = styled(Link)`
  text-decoration: none;
`;

export default function ActionsColumn({ row, children }: { row: Application, children: ReactNode }) {
  const { user } = useCombinedUser();
  const [reactivateModal, setReactivateModal] = useState<{id: string, open: boolean}>(defaultModal);
  const [rejectModal, setRejectModal] = useState<{id: string, open: boolean}>(defaultModal);
  const hasValidRole = isUserWithRoles([UserRoles.UNDERWRITER, UserRoles.SALES_SUPPORT], user);
  const shouldShowReject = hasValidRole
    && row.status
    && ['WithBroker', 'WithLender'].includes(row.status);
  const shouldShowReactivate = hasValidRole
    && row.status
    && ['Rejected', 'Withdrawn'].includes(row.status)
    && !(row.status === 'Rejected' && isUserWithRoles([UserRoles.SALES_SUPPORT], user));

  const onClickReactivate = () => {
    setReactivateModal({
      open: true,
      id: row.id,
    });
  };
  const onClickReject = () => {
    setRejectModal({
      open: true,
      id: row.id,
    });
  };

  return (
    <>
      <span>
        <ReactivateModal
          applicationId={reactivateModal.id}
          isOpen={reactivateModal.open}
          onClose={() => setReactivateModal(defaultModal)}
        />
        <RejectModal
          applicationId={rejectModal.id}
          isOpen={rejectModal.open}
          onClose={() => setRejectModal(defaultModal)}
        />
      </span>
      <ActionsColumnContainer>
        {children}
        {shouldShowReject && (
          <ActionButton data-testid={`btn-reject-${row.id}`} onClick={onClickReject}>
            <TableIcon className="mi-delete" />
          </ActionButton>
        )}
        {shouldShowReactivate && (
          <ActionButton data-testid={`btn-reactivate-${row.id}`} onClick={onClickReactivate}>
            <TableIcon className="mi-refresh" />
          </ActionButton>
        )}
        <PlainLink to={`/checklist/${row.id}`} data-testid={`btn-application-${row.id}`}>
          <ActionButton>
            <TableIcon className="mi-circle-arrow-right" />
          </ActionButton>
        </PlainLink>
      </ActionsColumnContainer>
    </>
  );
}
